/**
 * VISALL DataProcessor 主控制器
 * 统一数据处理入口，协调验证引擎、缓存管理、抽样策略
 */

import { ValidationEngine } from './ValidationEngine';
import { CacheManager } from './CacheManager';
import { SamplingStrategy } from './SamplingStrategy';
import type { ExtensionApi } from '../extension';
import type { Data, Spec, ValidationResult, DataProcessorOptions } from './types';

export class DataProcessor {
  private static instance: DataProcessor;

  private validationEngine: ValidationEngine;

  private cacheManager: CacheManager;

  private samplingStrategy: SamplingStrategy;

  private constructor(options?: DataProcessorOptions) {
    this.validationEngine = new ValidationEngine();
    this.cacheManager = new CacheManager(options?.cacheConfig);
    this.samplingStrategy = new SamplingStrategy(options?.samplingConfig);
  }

  /**
   * 获取单例实例
   */
  static getInstance(options?: DataProcessorOptions): DataProcessor {
    if (!DataProcessor.instance) {
      DataProcessor.instance = new DataProcessor(options);
    }
    return DataProcessor.instance;
  }

  /**
   * 核心验证和处理方法
   * 🚀 Phase 2: 集成缓存和抽样策略
   */
  validateAndProcess(data: Data, spec: Spec): ValidationResult {
    const startTime = performance.now();

    try {
      // 🔑 1. 生成缓存键
      const cacheKey = this.cacheManager.generateFingerprint(data, spec);

      // 🔍 2. 缓存检查
      const cached = this.cacheManager.get(cacheKey);
      if (cached) {
        console.log(`DataProcessor: Cache hit for validation ${cacheKey.slice(-8)}`);
        return cached.result;
      }

      // 📊 3. 数据抽样（如果需要）
      const processedData = this.applySmartSampling(data);
      if (processedData !== data) {
        console.log(
          `DataProcessor: Applied sampling, ${DataProcessor.getDataSize(
            data
          )} -> ${DataProcessor.getDataSize(processedData)} rows`
        );
      }

      // ✅ 4. 执行验证
      const result = this.validationEngine.validateLayers(processedData, spec);

      // 📈 5. 性能监控
      const duration = performance.now() - startTime;
      console.log(`DataProcessor validation completed in ${duration.toFixed(2)}ms`);

      // 💾 6. 缓存结果
      this.cacheManager.set(cacheKey, {
        result,
        timestamp: Date.now(),
        dataSize: DataProcessor.getDataSize(data),
        samplingApplied: processedData !== data
      });

      return result;
    } catch (error) {
      console.error('DataProcessor validation failed:', error);

      // 返回失败结果
      return {
        isValid: false,
        layerResults: new Map(),
        errors: [
          {
            type: 'business-rule',
            layer: 'unknown',
            message: `Validation failed: ${
              error instanceof Error ? error.message : 'Unknown error'
            }`,
            suggestion: 'Please check your data format and try again'
          }
        ],
        warnings: []
      };
    }
  }

  /**
   * 智能抽样应用
   */
  private applySmartSampling(data: Data): Data {
    return data.map(dataset => {
      if (!dataset.values || dataset.values.length === 0) {
        return dataset;
      }

      // 应用抽样策略
      const sampledValues = this.samplingStrategy.sample(dataset.values);

      return {
        ...dataset,
        values: sampledValues
      };
    });
  }

  /**
   * 获取数据大小
   */
  private static getDataSize(data: Data): number {
    return data.reduce((total, dataset) => {
      return total + (dataset.values?.length || 0);
    }, 0);
  }

  /**
   * 🎯 集成到Core渲染流程的静态方法
   * Phase 2: 支持缓存绑定和生命周期管理
   * 🆕 Phase 3: 集成编码适配，确保渲染使用适配后的编码
   */
  static integrateToMainView(api: ExtensionApi, spec: Spec): boolean {
    try {
      const processor = DataProcessor.getInstance();

      // 🔗 绑定API缓存到实例
      processor.bindToApiCache(api.cache);

      // 🆕 1. 编码适配 - 在验证和渲染之前转换所有图层的编码
      spec.layers.forEach((layer, index) => {
        const originalEncoding = layer.encoding || {};
        const adaptedLayer = ValidationEngine.adaptLayerEncodingForRendering(layer);

        if (JSON.stringify(adaptedLayer.encoding) !== JSON.stringify(originalEncoding)) {
          // 🎯 直接修改原始spec，确保后续渲染使用适配后的编码
          // eslint-disable-next-line no-param-reassign
          spec.layers[index] = {
            ...layer,
            encoding: adaptedLayer.encoding,
            // 🆕 保留适配信息用于调试
            __adaptationInfo: (adaptedLayer as unknown as { __adaptationInfo?: unknown })
              .__adaptationInfo
          };

          api.util.logger.info(
            'info',
            `🔄 Layer ${layer.type} encoding adapted for rendering:\n` +
              `  Original: ${JSON.stringify(originalEncoding, null, 2)}\n` +
              `  Adapted:  ${JSON.stringify(adaptedLayer.encoding, null, 2)}`
          );
        }
      });

      // 🆕 2. 验证（现在基于适配后的编码）
      const result = processor.validateAndProcess(api.data, spec);

      // 3. 设置验证标记（保持与现有逻辑兼容）
      spec.layers.forEach(layer => {
        const layerResult = result.layerResults.get(layer.type);
        // eslint-disable-next-line no-param-reassign
        layer.__isEncodingValid = layerResult?.isValid ?? false;
      });

      // 4. 记录验证结果
      if (!result.isValid) {
        api.util.logger.error('Invalid spec', result.errors);
      } else {
        console.log('DataProcessor validation passed for all layers');
      }

      return result.isValid;
    } catch (error) {
      api.util.logger.error('DataProcessor integration failed', error);
      return false;
    }
  }

  /**
   * 绑定到API实例缓存
   */
  bindToApiCache(apiCache: Map<string, unknown>): void {
    this.cacheManager.bindToApiCache(apiCache);
  }

  /**
   * 生命周期：图表销毁时的清理
   */
  static onChartDestroy(visId: string): void {
    CacheManager.clearInstanceCache(visId);
  }

  /**
   * 生命周期：图表更新时的缓存管理
   */
  onUpdate(oldSpec: Spec, newSpec: Spec): void {
    if (this.cacheManager.shouldClearOnUpdate(oldSpec, newSpec)) {
      console.log('DataProcessor: Clearing cache due to spec change');
      // 清理过期缓存
      this.cacheManager.cleanupExpiredEntries();
    }
  }

  /**
   * 重置实例（主要用于测试）
   */
  static resetInstance(): void {
    DataProcessor.instance = null as unknown as DataProcessor;
  }
}
